<script>
import {defineComponent, ref, onMounted, computed, watch} from "vue";
import {useRouter} from "vue-router";
import {useStore} from "vuex";
import {useMeta} from "vue-meta";
import {useError} from "@/hooks/useErrors";
import {useCountries} from "@/hooks/countries";

import AdminLoginForm from "@/components/forms/AdminLoginForm.vue";
import SelectPhoneModal from "@/components/modals/SelectPhoneModal.vue";
import EnterSmsCodeForm from "@/components/forms/EnterSmsCodeForm.vue";
import JSEncrypt from "jsencrypt";

const STEP_SMS_REQUEST = "STEP_SMS_REQUEST";
const STEP_SMS_CHECK = "STEP_SMS_CHECK";

export default defineComponent({
  layout: {name: 'LoginLayout'},
  components: {
    AdminLoginForm,
    SelectPhoneModal,
    EnterSmsCodeForm,
  },
  async beforeRouteEnter(to, from, next) {
    try {
      const {loadCountries} = useCountries();
      await loadCountries();
    } catch (e) {
      console.error(e);
    }

    next();
  },
  setup() {
    useMeta({
      title: 'Авторизация'
    });

    const store = useStore();
    const router = useRouter();
    const errors = useError();

    const currentStep = ref(STEP_SMS_CHECK);
    const countries = computed(() => store.getters.getCountriesList);

    const changeStep = (step) => {
      currentStep.value = step
    };

    const visible = ref(false);

    const toggleLoginHandler = async (phone) => {
      try {
        errors.clearErrors();
        const date = new Date()
        const hours = date.getUTCHours()
        const message = `${hours < 10 ? '0' + hours : hours}_${phone.replace(/[() \s]/g, '')}`
        const publicKey = '-----BEGIN PUBLIC KEY-----\n' +
            'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAxX87O00vA0xSZenYE8nm\n' +
            'W/p0/mZKT7M3kDeqWowDOjRB7IWhFrE/jWhArg3ZuLTvkyM2ZkwOuhYw9WJls5dn\n' +
            'VoTr3q7K8RAvOWymRoNDEZzOpUDEDNgUzhcnrOi5rgUc1udEQpaTAK35z/3AD4Sd\n' +
            'pS0pBWWQSuEMUR+PMpWUXALpb0Gbr5/J5qb1DG8y1+/USx66/HCcFzwR/m3FBFrB\n' +
            '2p/d1zHRp/QQDXbTLlaDphMw1kcHqlG4ShPFrLqZ01hj3xh/UxnHb4Da5z+lhOCr\n' +
            'kVjd7wvGBW7UiSOqwuu0QcJ+pM4T2kkq38BIlVEamWzmZdtODamDuF3CyCZkZNSj\n' +
            'DwIDAQAB\n' +
            '-----END PUBLIC KEY-----'
        const encryptor = new JSEncrypt();
        encryptor.setPublicKey(publicKey);
        const encrypted = encryptor.encrypt(message);
        await store.dispatch('fetchAuthorization', {phone: phone, secret: encrypted, phone_code: store.getters.getSelectCountry.phone_code });
        changeStep(STEP_SMS_REQUEST);
      } catch (e) {
        errors.setErrors(e.response.data);
      }
    }

    const toggleCheckCodeHandler = async (code) => {
      try {
        await store.dispatch('fetchVerificationCode', {
          phone: store.getters.getPhone,
          phoneCode: store.getters.getSelectCountry.phone_code,
          code,
        });

        await store.dispatch('fetchUpdateAuth', true);
        const user = store.getters.getProfile
        if (user.permissions.length) {
          if (user.permissions[0].name === 'states') {
            await router.push('/state-constructor');
          } else if (user.permissions[0].name === 'structure') {
            await router.push('/buildings');
          } else if (user.permissions[0].name === 'notifications') {
            await router.push('/notices');
          } else if (user.permissions[0].name === 'requisites') {
            await router.push('/vendors/list');
          } else if (user.permissions[0].name === 'site') {
            await router.push('/website');
          } else if (user.permissions[0].name === 'accounts') {
            await router.push('/personal-accounts');
          } else if (user.permissions[0].name === 'through-blocks') {
            await router.push('/blocks');
          }
          else {
            await router.push(`/${user.permissions[0].name}`);
          }
        } else {
          await router.push({name: 'error'});
        }
      } catch (e) {
        errors.setErrors(e.response.data);
        console.log(e)
      }
    }

    const toggleRetrySendCodeHandler = async () => {
      errors.clearErrors();
      await toggleLoginHandler(store.getters.getPhone);
    };

    const toggleChangeNumberHandler = () => {
      errors.clearErrors();
      changeStep(STEP_SMS_CHECK);
    };


    onMounted(async () => {
      // await store.dispatch('fetchCountries');
      store.commit('selectCountryByCountryName', 'Russia');
    });

    return {
      STEP_SMS_CHECK,
      STEP_SMS_REQUEST,
      visible,
      countries,
      currentStep,
      toggleRetrySendCodeHandler,
      toggleChangeNumberHandler,
      toggleCheckCodeHandler,
      toggleLoginHandler,
      changeStep,
      errors: errors.errors
    };
  }
});
</script>

<template>
  <SelectPhoneModal
      v-model:modelValue="visible"
      :countries="countries"
  />

  <div class="container">
    <div class="grid">
      <div class="hidden md:block md:col-7 h-screen background-login">
      </div>
      <div class="col-12 md:col-5 py-0 px-3">
        <div class="grid justify-content-center h-screen">
          <div class="col-12 p-0">
            <div class="flex align-items-center h-screen">
              <div class="form w-full mx-2">
                <img class="gap-3 w-4rem mb-3" src="../assets/images/admin/Logo.png" alt="">
                <p class="mb-5 text-left text-xl font-bold">Добро пожаловать</p>

                <AdminLoginForm
                    v-if="currentStep === STEP_SMS_CHECK"
                    :errors="errors"
                    @toggleSelectPhone="visible = true"
                    @toggleLogin="toggleLoginHandler"
                />

                <EnterSmsCodeForm
                    v-if="currentStep === STEP_SMS_REQUEST"
                    @toggleChangeNumber="toggleChangeNumberHandler"
                    @toggleCheckCode="toggleCheckCodeHandler"
                    @toggleRetrySendCode="toggleRetrySendCodeHandler"
                    :errors="errors"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.background-login {
  background: url('../assets/images/admin/Image.png') no-repeat center;
  background-size: cover;
}
</style>
